import { Controller } from "@hotwired/stimulus"

/*
  CopyTextController

  You can use this controller to copy text to the clipboard.

  Basic Example:
  <span data-controller="copy-text">
    This text can be copied
  </span>

  With specific text target:
  <div data-controller="copy-text">
    <span>Label:</span>
    <span data-copy-text-target="text">api-key-12345</span>
  </div>
*/

export default class extends Controller {
  static targets = ["text"]
  static classes = ["copied"]

  connect() {
    this.createCopyButton()
  }

  createCopyButton() {
    const copyButton = document.createElement("button")
    copyButton.type = "button"
    copyButton.className = "copy-text-button"
    copyButton.innerHTML = this.getCopyIcon()
    copyButton.setAttribute("title", "Copy to clipboard")

    copyButton.addEventListener("click", this.copy.bind(this))

    this.element.style.position = "relative"
    this.element.style.display = "inline-block"

    // Style the button
    copyButton.style.cssText = `
      position: absolute;
      right: -24px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      opacity: 0.6;
      transition: opacity 0.2s ease;
    `

    // Add hover effect
    copyButton.addEventListener("mouseenter", () => {
      copyButton.style.opacity = "1"
    })

    copyButton.addEventListener("mouseleave", () => {
      copyButton.style.opacity = "0.6"
    })

    // Append the button to the element
    this.element.appendChild(copyButton)
    this.copyButton = copyButton
  }

  async copy() {
    const textToCopy = this.getTextContent()

    await navigator.clipboard.writeText(textToCopy)
    this.showCopiedState()
  }

  getTextContent() {
    if (this.hasTextTarget) {
      return this.textTarget.textContent.trim()
    }
    return this.element.textContent.trim()
  }

  showCopiedState() {
    const originalIcon = this.copyButton.innerHTML
    const originalTitle = this.copyButton.getAttribute("title")

    // Update button to show copied state
    this.copyButton.innerHTML = this.getCheckIcon()
    this.copyButton.setAttribute("title", "Copied!")

    // Reset after 2 seconds
    setTimeout(() => {
      this.copyButton.innerHTML = originalIcon
      this.copyButton.setAttribute("title", originalTitle)
    }, 2000)
  }

  getCopyIcon() {
    return `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
      </svg>
    `
  }

  getCheckIcon() {
    return `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="20,6 9,17 4,12"></polyline>
      </svg>
    `
  }

  disconnect() {
    // Clean up event listeners if needed
    if (this.copyButton) {
      this.copyButton.removeEventListener("click", this.copy.bind(this))
    }
  }
}
